import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TeamService } from '../../services/team.service';
import { AuthService, User } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import {
  Team,
  TeamMember,
  TeamInvitation,
  TeamResponse,
} from '@spectrace/shared';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-settings-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './settings-modal.component.html',
  styleUrls: ['./settings-modal.component.css'],
})
export class SettingsModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  private readonly allowedAvatarMimeTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
  ]);
  private readonly maxAvatarUploadBytes = 8 * 1024 * 1024; // 8 MB input cap
  private readonly maxAvatarPayloadBytes = 90 * 1024; // keep API payload small
  private readonly minAvatarDimensionPx = 160;
  private readonly cropFrameSizePx = 280;
  readonly maxAvatarZoom = 4;
  private authSubscription?: Subscription;
  private cropDragPointerId: number | null = null;
  private cropDragLastX = 0;
  private cropDragLastY = 0;

  // View state
  currentView: 'teams' | 'create-team' | 'team-details' | 'invitations' =
    'teams';
  showSupportDialog = false;

  // Data
  teams: Team[] = [];
  selectedTeam: TeamResponse | null = null;
  pendingInvitations: TeamInvitation[] = [];
  currentUser: User | null = null;

  // Forms
  newTeamName = '';
  newTeamDescription = '';
  inviteEmail = '';
  generatedInviteToken = '';
  generatedInviteLink = '';
  supportMessage = '';

  // UI state
  loading = false;
  supportSubmitting = false;
  supportError = '';
  supportSuccessMessage = '';
  error = '';
  successMessage = '';
  avatarSaving = false;
  nicknameSaving = false;
  nicknameDraft = '';
  private nicknameDirty = false;
  avatarCropOpen = false;
  avatarSourceDataUrl: string | null = null;
  avatarSourceImage: HTMLImageElement | null = null;
  avatarZoom = 1;
  avatarOffsetX = 0;
  avatarOffsetY = 0;

  constructor(
    private teamService: TeamService,
    private authService: AuthService,
    private apiService: ApiService,
  ) {}

  ngOnInit() {
    this.currentUser = this.authService.getCurrentUser();
    this.nicknameDraft = (this.currentUser?.nickname || '').trim();
    this.authSubscription = this.authService.currentUser$.subscribe((user) => {
      this.currentUser = user;
      if (!this.nicknameDirty) {
        this.nicknameDraft = (user?.nickname || '').trim();
      }
    });
    this.loadTeams();
    this.loadPendingInvitations();
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  loadTeams() {
    this.loading = true;
    this.error = '';
    this.teamService.getUserTeams().subscribe({
      next: (response) => {
        this.teams = response.teams;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.error || 'Failed to load teams';
        this.loading = false;
      },
    });
  }

  loadPendingInvitations() {
    this.teamService.getPendingInvitations().subscribe({
      next: (response) => {
        this.pendingInvitations = response.invitations;
      },
      error: (err) => {
        console.error('Failed to load invitations:', err);
      },
    });
  }

  get profileInitial(): string {
    const name =
      this.currentUser?.nickname?.trim() || this.currentUser?.name?.trim();
    if (name) {
      return name.charAt(0).toUpperCase();
    }
    return '?';
  }

  get profileDisplayName(): string {
    return (
      this.currentUser?.nickname?.trim() ||
      this.currentUser?.name?.trim() ||
      'Unknown Driver'
    );
  }

  onNicknameDraftChanged(value: string): void {
    this.nicknameDirty = true;
    this.nicknameDraft = value;
  }

  saveNickname(): void {
    if (this.nicknameSaving || !this.currentUser) {
      return;
    }

    const trimmedNickname = this.nicknameDraft.trim();
    const currentNickname = (this.currentUser.nickname || '').trim();
    if (trimmedNickname === currentNickname) {
      this.nicknameDirty = false;
      return;
    }

    this.nicknameSaving = true;
    this.error = '';
    this.successMessage = '';

    this.authService
      .updateProfile({ nickname: trimmedNickname.length > 0 ? trimmedNickname : null })
      .subscribe({
        next: (updatedUser) => {
          this.nicknameSaving = false;
          this.nicknameDirty = false;
          this.nicknameDraft = (updatedUser.nickname || '').trim();
          this.successMessage = 'Nickname updated.';
          setTimeout(() => {
            if (this.successMessage === 'Nickname updated.') {
              this.successMessage = '';
            }
          }, 3000);
        },
        error: (err) => {
          this.nicknameSaving = false;
          this.error = err.error?.error || 'Failed to update nickname';
        },
      });
  }

  get avatarRenderedWidthPx(): number {
    if (!this.avatarSourceImage) {
      return 0;
    }
    return this.avatarSourceImage.naturalWidth * this.getAvatarScale();
  }

  get avatarRenderedHeightPx(): number {
    if (!this.avatarSourceImage) {
      return 0;
    }
    return this.avatarSourceImage.naturalHeight * this.getAvatarScale();
  }

  get avatarTranslateXPx(): number {
    return (this.cropFrameSizePx - this.avatarRenderedWidthPx) / 2 + this.avatarOffsetX;
  }

  get avatarTranslateYPx(): number {
    return (this.cropFrameSizePx - this.avatarRenderedHeightPx) / 2 + this.avatarOffsetY;
  }

  openAvatarPicker(fileInput: HTMLInputElement): void {
    if (this.avatarSaving) {
      return;
    }
    fileInput.click();
  }

  async onAvatarFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    if (!file) {
      return;
    }

    if (!this.allowedAvatarMimeTypes.has(file.type.toLowerCase())) {
      this.error = 'Please upload a PNG, JPG, or WEBP image.';
      return;
    }

    if (file.size > this.maxAvatarUploadBytes) {
      this.error = 'Image must be 8MB or smaller.';
      return;
    }

    try {
      const { dataUrl, image } = await this.loadImageFromFile(file);
      if (
        image.naturalWidth < this.minAvatarDimensionPx ||
        image.naturalHeight < this.minAvatarDimensionPx
      ) {
        this.error = `Image must be at least ${this.minAvatarDimensionPx} x ${this.minAvatarDimensionPx} pixels.`;
        return;
      }

      this.avatarSourceDataUrl = dataUrl;
      this.avatarSourceImage = image;
      this.avatarZoom = 1;
      this.avatarOffsetX = 0;
      this.avatarOffsetY = 0;
      this.clampAvatarOffsets();
      this.avatarCropOpen = true;
      this.error = '';
    } catch (err) {
      console.error('Failed to parse avatar file:', err);
      this.error = 'Failed to read the image file.';
    }
  }

  closeAvatarCropper(): void {
    this.avatarCropOpen = false;
    this.avatarSourceDataUrl = null;
    this.avatarSourceImage = null;
    this.avatarZoom = 1;
    this.avatarOffsetX = 0;
    this.avatarOffsetY = 0;
    this.cropDragPointerId = null;
  }

  onAvatarZoomInput(): void {
    this.clampAvatarOffsets();
  }

  onAvatarCropPointerDown(event: PointerEvent): void {
    if (!this.avatarSourceImage) {
      return;
    }

    this.cropDragPointerId = event.pointerId;
    this.cropDragLastX = event.clientX;
    this.cropDragLastY = event.clientY;
    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  @HostListener('window:pointermove', ['$event'])
  onWindowPointerMove(event: PointerEvent): void {
    if (this.cropDragPointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.cropDragLastX;
    const deltaY = event.clientY - this.cropDragLastY;
    this.cropDragLastX = event.clientX;
    this.cropDragLastY = event.clientY;

    this.avatarOffsetX += deltaX;
    this.avatarOffsetY += deltaY;
    this.clampAvatarOffsets();
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onWindowPointerEnd(event: PointerEvent): void {
    if (this.cropDragPointerId !== event.pointerId) {
      return;
    }
    this.cropDragPointerId = null;
  }

  saveCroppedAvatar(): void {
    if (!this.avatarSourceImage) {
      return;
    }

    const croppedDataUrl = this.generateCroppedAvatarDataUrl();
    if (!croppedDataUrl) {
      this.error = 'Unable to process this image. Try a different one.';
      return;
    }

    this.avatarSaving = true;
    this.error = '';
    this.successMessage = '';

    this.authService.updateProfile({ avatarUrl: croppedDataUrl }).subscribe({
      next: () => {
        this.avatarSaving = false;
        this.closeAvatarCropper();
        this.successMessage = 'Profile picture updated.';
        setTimeout(() => {
          if (this.successMessage === 'Profile picture updated.') {
            this.successMessage = '';
          }
        }, 3000);
      },
      error: (err) => {
        this.avatarSaving = false;
        this.error = err.error?.error || 'Failed to update profile picture';
      },
    });
  }

  removeProfileAvatar(): void {
    if (!this.currentUser?.avatarUrl || this.avatarSaving) {
      return;
    }

    this.avatarSaving = true;
    this.error = '';
    this.successMessage = '';

    this.authService.updateProfile({ avatarUrl: null }).subscribe({
      next: () => {
        this.avatarSaving = false;
        this.successMessage = 'Profile picture removed.';
        setTimeout(() => {
          if (this.successMessage === 'Profile picture removed.') {
            this.successMessage = '';
          }
        }, 3000);
      },
      error: (err) => {
        this.avatarSaving = false;
        this.error = err.error?.error || 'Failed to remove profile picture';
      },
    });
  }

  private getAvatarScale(): number {
    if (!this.avatarSourceImage) {
      return 1;
    }
    const fitScale = Math.max(
      this.cropFrameSizePx / this.avatarSourceImage.naturalWidth,
      this.cropFrameSizePx / this.avatarSourceImage.naturalHeight,
    );
    return fitScale * this.avatarZoom;
  }

  private clampAvatarOffsets(): void {
    const maxOffsetX = Math.max((this.avatarRenderedWidthPx - this.cropFrameSizePx) / 2, 0);
    const maxOffsetY = Math.max((this.avatarRenderedHeightPx - this.cropFrameSizePx) / 2, 0);

    this.avatarOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, this.avatarOffsetX));
    this.avatarOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, this.avatarOffsetY));
  }

  private estimateDataUrlBytes(dataUrl: string): number {
    const base64Data = dataUrl.split(',')[1] || '';
    return Math.floor((base64Data.length * 3) / 4);
  }

  private generateCroppedAvatarDataUrl(): string | null {
    if (!this.avatarSourceImage) {
      return null;
    }

    const renderWidth = this.avatarRenderedWidthPx;
    const renderHeight = this.avatarRenderedHeightPx;
    const imageLeft = (this.cropFrameSizePx - renderWidth) / 2 + this.avatarOffsetX;
    const imageTop = (this.cropFrameSizePx - renderHeight) / 2 + this.avatarOffsetY;

    const compressionAttempts = [
      { size: 384, quality: 0.9 },
      { size: 320, quality: 0.85 },
      { size: 256, quality: 0.8 },
    ];

    for (const { size, quality } of compressionAttempts) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        continue;
      }

      ctx.fillStyle = '#10131a';
      ctx.fillRect(0, 0, size, size);
      const scale = size / this.cropFrameSizePx;
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.avatarSourceImage, imageLeft, imageTop, renderWidth, renderHeight);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (this.estimateDataUrlBytes(dataUrl) <= this.maxAvatarPayloadBytes) {
        return dataUrl;
      }
    }

    return null;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('Invalid file payload'));
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  private loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to decode image'));
      image.src = dataUrl;
    });
  }

  private async loadImageFromFile(file: File): Promise<{ dataUrl: string; image: HTMLImageElement }> {
    const dataUrl = await this.readFileAsDataUrl(file);
    const image = await this.loadImageFromDataUrl(dataUrl);
    return { dataUrl, image };
  }

  showCreateTeamForm() {
    this.currentView = 'create-team';
    this.newTeamName = '';
    this.newTeamDescription = '';
    this.error = '';
    this.successMessage = '';
  }

  showTeamDetails(team: Team) {
    this.loading = true;
    this.error = '';
    this.generatedInviteToken = '';
    this.generatedInviteLink = '';
    this.teamService.getTeamDetails(team.id).subscribe({
      next: (response) => {
        this.selectedTeam = response;
        this.currentView = 'team-details';
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.error || 'Failed to load team details';
        this.loading = false;
      },
    });
  }

  showInvitations() {
    this.currentView = 'invitations';
    this.error = '';
    this.successMessage = '';
  }

  backToTeams() {
    this.currentView = 'teams';
    this.selectedTeam = null;
    this.error = '';
    this.successMessage = '';
  }

  createTeam() {
    if (!this.newTeamName.trim()) {
      this.error = 'Team name is required';
      return;
    }

    this.loading = true;
    this.error = '';
    this.successMessage = '';

    this.teamService
      .createTeam({
        name: this.newTeamName.trim(),
        description: this.newTeamDescription.trim() || undefined,
      })
      .subscribe({
        next: (response) => {
          this.successMessage = 'Team created successfully!';
          this.teams.push(response.team);
          this.loading = false;

          // Go back to teams view after a short delay
          setTimeout(() => {
            this.backToTeams();
          }, 1500);
        },
        error: (err) => {
          this.error = err.error?.error || 'Failed to create team';
          this.loading = false;
        },
      });
  }

  inviteMember() {
    if (!this.selectedTeam || !this.inviteEmail.trim()) {
      this.error = 'Email is required';
      return;
    }

    this.loading = true;
    this.error = '';
    this.successMessage = '';
    this.generatedInviteToken = '';
    this.generatedInviteLink = '';

    this.teamService
      .inviteToTeam(this.selectedTeam.team.id, {
        email: this.inviteEmail.trim(),
      })
      .subscribe({
        next: (response) => {
          this.successMessage = 'Invitation sent successfully!';
          this.generatedInviteToken = response.invitation.token;
          this.generatedInviteLink = `${window.location.origin}/onboarding?inviteToken=${response.invitation.token}`;
          this.inviteEmail = '';
          this.loading = false;

          // Clear success message after delay
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (err) => {
          this.error = err.error?.error || 'Failed to send invitation';
          this.loading = false;
        },
      });
  }

  createInviteToken(): void {
    if (!this.selectedTeam) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.successMessage = '';
    this.generatedInviteToken = '';
    this.generatedInviteLink = '';

    this.teamService.createInviteToken(this.selectedTeam.team.id).subscribe({
      next: (response) => {
        this.successMessage = 'Invite token created successfully!';
        this.generatedInviteToken = response.invitation.token;
        this.generatedInviteLink = `${window.location.origin}/onboarding?inviteToken=${response.invitation.token}`;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.error || 'Failed to create invite token';
        this.loading = false;
      },
    });
  }

  async copyInviteToken(): Promise<void> {
    if (!this.generatedInviteToken) {
      return;
    }

    const copied = await this.copyText(this.generatedInviteToken);
    this.successMessage = copied
      ? 'Invite token copied to clipboard.'
      : 'Failed to copy invite token.';
  }

  async copyInviteLink(): Promise<void> {
    if (!this.generatedInviteLink) {
      return;
    }

    const copied = await this.copyText(this.generatedInviteLink);
    this.successMessage = copied
      ? 'Invite link copied to clipboard.'
      : 'Failed to copy invite link.';
  }

  private async copyText(value: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }

      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }

  acceptInvitation(invitation: TeamInvitation) {
    this.loading = true;
    this.error = '';
    this.successMessage = '';

    this.teamService
      .acceptInvitation({
        token: invitation.token,
      })
      .subscribe({
        next: () => {
          this.successMessage = `Joined ${invitation.teamName} successfully!`;
          this.pendingInvitations = this.pendingInvitations.filter(
            (i) => i.id !== invitation.id,
          );
          this.loadTeams(); // Reload teams to show the new one
          this.loading = false;

          // Clear success message after delay
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (err) => {
          this.error = err.error?.error || 'Failed to accept invitation';
          this.loading = false;
        },
      });
  }

  openSupportDialog(): void {
    this.showSupportDialog = true;
    this.supportMessage = '';
    this.supportError = '';
    this.supportSuccessMessage = '';
  }

  closeSupportDialog(): void {
    this.showSupportDialog = false;
    this.supportSubmitting = false;
    this.supportError = '';
    this.supportSuccessMessage = '';
  }

  submitSupportMessage(): void {
    const message = this.supportMessage.trim();
    if (!message) {
      this.supportError = 'Message is required';
      return;
    }

    this.supportSubmitting = true;
    this.supportError = '';
    this.supportSuccessMessage = '';

    this.apiService.post<{ success: boolean }>('/support/message', { message }).subscribe({
      next: () => {
        this.supportSubmitting = false;
        this.supportSuccessMessage = 'Support message sent successfully.';
        this.supportMessage = '';
      },
      error: (err) => {
        this.supportSubmitting = false;
        this.supportError = err.error?.error || 'Failed to send support message';
      }
    });
  }

  removeMember(member: TeamMember) {
    if (!this.selectedTeam) return;

    if (!confirm(`Remove ${member.userName} from the team?`)) {
      return;
    }

    this.loading = true;
    this.error = '';

    this.teamService
      .removeMember(this.selectedTeam.team.id, member.userId)
      .subscribe({
        next: () => {
          this.successMessage = 'Member removed successfully';
          this.selectedTeam!.members = this.selectedTeam!.members.filter(
            (m) => m.id !== member.id,
          );
          this.loading = false;

          // Clear success message after delay
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (err) => {
          this.error = err.error?.error || 'Failed to remove member';
          this.loading = false;
        },
      });
  }

  onClose() {
    this.closeAvatarCropper();
    this.closeSupportDialog();
    this.close.emit();
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'owner':
        return 'badge-owner';
      case 'admin':
        return 'badge-admin';
      case 'trial':
        return 'badge-trial';
      default:
        return 'badge-member';
    }
  }
}
